import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { uniqBy } from 'lodash'
import { isAddress } from 'viem'

import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useBatchFarcasterLookup } from '~/hooks/useFarcaster/farcasterBatchAddressLookup'
import { useServerRequests } from '~/hooks/useRequests'
import { AddressSearchResult, IAddress, NeynarUser } from '~/types'

type FarcasterSearchResult = Pick<AddressSearchResult, 'farcaster' | 'address'>

export const useFarcasterSearch = (queryInput?: string) => {
  const queryClient = useQueryClient()
  const {
    addressSearchHistory: { searchHistory },
  } = useAuth()
  const { serverGetRequest } = useServerRequests()
  const debouncedQueryInput = useDebounce(queryInput, 100)

  const query = useQuery<FarcasterSearchResult[]>({
    queryKey: [QUERY_KEYS.FARCASTER_SEARCH, debouncedQueryInput],
    queryFn: async () => {
      // if the query is an address, return an empty list
      if (debouncedQueryInput && isAddress(debouncedQueryInput)) return []

      const username = debouncedQueryInput?.replace(/@/g, '') ?? ''

      if (!username) return []

      const response = await serverGetRequest<{ users: NeynarUser[] }>({
        path: '/api/farcaster/search-by-username',
        params: { username },
      })

      if (!response?.data?.users) return []

      const farcasterResults: FarcasterSearchResult[] =
        response.data.users
          .filter(
            (user) =>
              user.verified_addresses.primary ||
              user.verified_addresses.eth_addresses.length > 0,
          )
          .map(({ fid, username, pfp_url, verified_addresses }) => ({
            address:
              verified_addresses.primary ?? verified_addresses.eth_addresses[0], // eth verified addresses are sorted reverse chronological order
            farcaster: {
              username,
              pfp: pfp_url,
              fid,
              displayName: `@${username}`,
            },
          })) ?? []

      farcasterResults.forEach((result) => {
        queryClient.setQueryData(
          [QUERY_KEYS.FARCASTER_LOOKUP, result.address],
          result.farcaster,
        )
      })

      return farcasterResults
    },
    initialData: () => {
      const queryUsername = debouncedQueryInput?.slice(1)
      const matches = searchHistory.filter((result) => {
        return result.farcaster && result.farcaster.username === queryUsername
      })
      return matches.length > 0 ? matches : undefined
    },
    retry: false,
    enabled: !!debouncedQueryInput,
  })

  return {
    results: query.data,
    isLoading: query.isLoading,
  }
}

export const useFarcasterLookup = (address?: IAddress) => {
  const queryClient = useQueryClient()
  const {
    addressSearchHistory: { searchHistory },
  } = useAuth()

  const batchLookup = useBatchFarcasterLookup()

  const query = useQuery<AddressSearchResult['farcaster']>({
    queryKey: [QUERY_KEYS.FARCASTER_LOOKUP, address],
    queryFn: async () => {
      if (address?.startsWith('@')) return undefined
      if (!address) throw new Error('No address provided')

      const results = await batchLookup.call(address)
      if (!results || results.length === 0)
        throw new Error('No farcaster data found')

      results.forEach((result) => {
        // Fan out cache to all verified addresses for future hits
        uniqBy(
          [
            result.verified_addresses?.primary,
            ...(result.verified_addresses?.eth_addresses ?? []),
          ],
          'address',
        ).forEach((address) => {
          queryClient.setQueryData([QUERY_KEYS.FARCASTER_LOOKUP, address], {
            fid: result.fid,
            pfp: result.pfp,
            username: result.username,
            displayName: result.display_name,
          })
        })
      })

      return results[0]
    },
    initialData: () =>
      searchHistory.find((r) => r.address === address && r.farcaster)
        ?.farcaster,
    enabled: !!address && isAddress(address),
    retry: false,
    retryOnMount: false,
    staleTime: Infinity, // Considered fresh indefinitely
  })

  return { farcaster: query.data }
}
